import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { useAuth } from '../../services/authentication/authContext';
import { teamServices } from '../../services/teamServices';
import JobForm from '../job/JobForm';
import Clients from '../client/Clients';
import ClientForm from '../client/ClientForm';
import Jobs from '../job/Jobs';
import JobStatus from '../job/jobStatus';
import UserForm from '../user/UserForm';
import TaskForm from '../task/TaskForm';
import Tasks from '../task/Tasks';
import JobTrack from '../job/JobTrack';
import Users from '../user/Users';
import ExtraTaskForm from '../task/ExtraTaskForm';
import ExtraTasks from '../task/ExtraTasks';
import ClientWiseJobReport from '../report/clientWiseJobReport';
import JobStatusReport from '../report/jobStatusReport';
import AssignedJob from '../task/AssignedJob';
import UnassignedJob from '../task/UnAssignedJob.js';
import Reports from '../report/reports';
import JobCompletionReport from '../report/jobCompletionReport';
import MissedExDateReport from '../report/missedExDateReport';
import EmpEfficiencyReport from '../report/empEfficiencyReport';
import Login from '../login/Login';
import Profile from '../profile/Profile';
import { fetchUserDetails, logout } from '../redux/userDetailsSlice';
import { useDispatch, useSelector } from 'react-redux';
import Teams from '../team/Teams';
import AssistanceTeams from '../team/AssistanceTeams';
import TeamMembers from '../team/TeamMembers';
import TaskTimeTrack from '../task/TaskTimeTrack';
import EmpJobTaskReport from '../report/empJobTaskReport.js';
import EmpWiseJobReport from '../report/empWiseJobReport.js';
import EmpExtraTaskReport from '../report/empExtraTaskReport.js';
import JobWiseReport from '../report/jobWiseReport.js';
import Dashboard from '../../dashboard/Dashboard.js';
import DocumentForm from '../document/DocumentForm.js';
import DocumentPreview from '../document/DocumentPreview.js';
import InvoiceForm from '../invoice/InvoiceForm.js';
import ApplyLeave from '../leave/ApplyLeave.js';
import LeaveApproval from '../leave/LeaveApproval.js';
import MyLeaves from '../leave/MyLeaves.js';
import LeaveHistory from '../leave/LeaveHistory.js';
import HolidayManagement from '../holiday/HolidayManagement.js';
import LeaveBalanceManagement from '../leave/LeaveBalanceManagement.js';
import LeaveBalanceAuditPage from '../leave/LeaveBalanceAuditPage.js';
import LeaveReport from '../report/leaveReport.js';
import EmployeeMonitoring from '../performance/EmployeeMonitoring.js';
import SelfEvaluation from '../performance/SelfEvaluation.js';
import QuarterlyReviewForm from '../performance/QuarterlyReviewForm.js';
import InvoicePreview from '../invoice/InvoicePreview.js';
import DashboardShellSkeleton from '../common/DashboardShellSkeleton';
import ErrorState from '../common/ErrorState';
import DashboardLayout from '../../layout/DashboardLayout';
import NotificationBell from '../notification/NotificationBell';
import NotificationsPage from '../notification/NotificationsPage';
import { NotificationProvider } from '../../context/NotificationContext';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import BeachAccessRoundedIcon from '@mui/icons-material/BeachAccessRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

const Home = () => {
  const { t } = useTranslation();
  const { accessToken, removeTokens } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [userName, setUsername] = useState('');
  const { data: userData, loading, error } = useSelector(state => state.userDetails);
  const userId = userData?.id;
  const dispatch = useDispatch();
  const location = useLocation();
  const goBack = () => navigate(-1);

  // Define subpages where back button should be shown
  const showBackButtonPaths = [
    '/home/extra-task',
    '/home/client',
    '/home/job',
    '/home/task',
    '/home/user',
    '/home/task-time-track',
    '/home/team-members',
    '/home/leave-balance-audit'
  ];

  // Show button if current location matches any subpage
  const showBackButton = showBackButtonPaths.includes(location.pathname);

  useEffect(() => {
    if (accessToken && !userData?.id) {
      try {
        const decodedToken = jwtDecode(accessToken);

        let extractedRoles = [];

        if (Array.isArray(decodedToken.roles)) {
          extractedRoles = decodedToken.roles;
        } else if (typeof decodedToken.roles === "string") {
          extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
        }

        setRoles(extractedRoles);
        const userEmail = decodedToken.sub;
        dispatch(fetchUserDetails(userEmail));
      } catch (error) {
        console.error('Invalid token', error);
        navigate('/login');
      }
    } else if (!accessToken) {
      navigate('/login');
    }
  }, [accessToken, userData?.id, dispatch, navigate]);

  useEffect(() => {
    if (userData) {
      setUsername(userData.firstName + " " + userData.lastName);
      localStorage.setItem('userName', userData.firstName + " " + userData.middleName + " " + userData.lastName);
      localStorage.setItem('userId', userData.id);
    }
  }, [userData]);

  const [teamOwnerIds, setTeamOwnerIds] = useState([]);

  useEffect(() => {
    if (accessToken) {
      teamServices.getAllOwnerIdsList()
        .then((data) => {
          if (Array.isArray(data)) {
            setTeamOwnerIds(data);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch team owner ids', err);
        });
    }
  }, [accessToken]);

  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const isAdmin = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN');
  const isSubAdmin = roles.includes('SUB_ADMIN');
  const isMainTeamLeader = isAdmin || (userId && teamOwnerIds.some(id => Number(id) === Number(userId)));
  const isTeamLeader = roles.includes('MANAGE_JOBS') || isAdmin;
  const showJobComponents = isAdmin || isSubAdmin || roles.includes('MANAGE_JOBS');
  const showClientTab = isAdmin || roles.includes('MANAGE_CLIENTS');
  const showUserComponents = isAdmin || roles.includes('MANAGE_USERS');
  const showTaskComponents = isAdmin || isSubAdmin || roles.includes('MANAGE_TASKS');
  const showAssignedJobTab = isSubAdmin || roles.includes('MANAGE_TASKS');
  const showUnassignedJobTab = isSubAdmin || roles.includes('MANAGE_JOBS');
  const showReportComponents = isAdmin || isSubAdmin || roles.includes('MANAGE_TASKS');
  const showReportComponentForTeamLeader = roles.includes('MANAGE_JOBS');
  const showDocumentComponents = isAdmin;
  const showInvoiceComponents = isSuperAdmin;
  const showEmployeeMonitoring = isAdmin || roles.includes('MANAGE_JOBS');
  const showMyReviews = !isSuperAdmin; // Super Admins do not need self-evaluation

  const showApplyLeave = true;
  // Leave Approval: only Main Team Leaders and Admins (hidden for Assistant Team Leaders)
  const showLeaveApproval = isMainTeamLeader;
  // My Leaves: standard employees and Sub-Admins
  const showMyLeaves =
    isSubAdmin ||
    (roles.includes('MANAGE_TASKS') &&
      !roles.includes('MANAGE_JOBS') &&
      !isAdmin);
  // Leave History: TL, Admin, SuperAdmin
  const showLeaveHistory = isAdmin || roles.includes('MANAGE_JOBS');
  // Leave Report: Admin, TL
  const showLeaveReport = isAdmin || roles.includes('MANAGE_JOBS');
  // Holiday Management: Admin / SuperAdmin only
  const showHolidayManagement = isAdmin;

  const showShellLoading = Boolean(accessToken) && loading && !userData?.id;
  const showShellError = Boolean(accessToken) && error;

  if (showShellLoading) {
    return <DashboardShellSkeleton />;
  }

  if (showShellError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'var(--secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <ErrorState
          title="Failed to load your account"
          description="Please try again. If the problem persists, sign in again."
          details={typeof error === 'string' ? error : undefined}
          action={
            <Button
              variant="contained"
              sx={{ mt: 1, backgroundColor: 'var(--primary)' }}
              onClick={() => navigate('/login')}
            >
              Go to login
            </Button>
          }
        />
      </Box>
    );
  }

  const logoutApp = () => {
    dispatch(logout());
    removeTokens();
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
    navigate('/login');
  };

  const navSections = [
    {
      id: 'overview',
      label: 'OVERVIEW',
      defaultOpen: true,
      collapsible: true,
      items: [
        { to: '/home/dashboard', label: 'Dashboard', icon: <BarChartRoundedIcon fontSize="small" />, end: false },
      ],
    },
    {
      id: 'work',
      label: 'WORK',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showClientTab ? [{ to: '/home/clients', label: t('Client'), icon: <BusinessRoundedIcon fontSize="small" /> }] : []),
        ...(showJobComponents ? [{ to: '/home/jobs', label: t('Job'), icon: <WorkRoundedIcon fontSize="small" /> }] : []),
        ...(showJobComponents ? [{ to: '/home/job-status', label: t('Job Status'), icon: <TaskAltRoundedIcon fontSize="small" /> }] : []),
        ...(showJobComponents ? [{ to: '/home/job-track', label: t('Job Track'), icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
        ...(showAssignedJobTab ? [{ to: '/home/assigned-job', label: t('Assigned Job'), icon: <AssignmentRoundedIcon fontSize="small" /> }] : []),
        ...(showUnassignedJobTab ? [{ to: '/home/unassigned-job', label: t('Unassigned Job'), icon: <AssignmentRoundedIcon fontSize="small" /> }] : []),
      ],
    },
    {
      id: 'tasks',
      label: 'TASKS',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showTaskComponents ? [{ to: '/home/tasks', label: t('Task'), icon: <AssignmentRoundedIcon fontSize="small" /> }] : []),
        ...(showTaskComponents ? [{ to: '/home/extra-tasks', label: t('Extra Task'), icon: <AssignmentRoundedIcon fontSize="small" /> }] : []),
      ],
    },
    {
      id: 'people',
      label: 'PEOPLE',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showUserComponents ? [{ to: '/home/users', label: t('User'), icon: <GroupsRoundedIcon fontSize="small" /> }] : []),
        ...(showUserComponents ? [{
          to: '/home/teams',
          label: t('Team'),
          icon: <GroupsRoundedIcon fontSize="small" />,
          items: [
            { to: '/home/assistance-teams', label: 'Assistance Teams', icon: <GroupAddRoundedIcon fontSize="small" /> },
          ]
        }] : []),
        { to: '/home/profile', label: 'Profile', icon: <PersonRoundedIcon fontSize="small" /> },
      ],
    },
    {
      id: 'reports',
      label: 'REPORTS',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showReportComponents ? [{
          to: '/home/reports', label: t('Report'), icon: <AssessmentRoundedIcon fontSize="small" />,
          items: [
            ...(showReportComponents ? [{ to: '/home/client-job-report', label: 'Client-wise Job Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...((isAdmin || isSubAdmin || showReportComponentForTeamLeader) ? [{ to: '/home/job-status-report', label: 'Job Status Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...((isAdmin || showReportComponentForTeamLeader) ? [{ to: '/home/job-completion-report', label: 'Job Completion Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...((isAdmin || isSubAdmin) ? [{ to: '/home/missed-ex-date-report', label: 'Missed Expected Date Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...(isAdmin ? [{ to: '/home/employee-efficiency-report', label: 'Employee Efficiency Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...((isAdmin || isSubAdmin) ? [{ to: '/home/employee-extra-task-report', label: 'Employee Extra Tasks Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...(showReportComponents && !isSubAdmin ? [{ to: '/home/employee-job-task-report', label: 'Employee Day Wise Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...(showReportComponentForTeamLeader ? [{ to: '/home/employee-wise-job-report', label: 'Employee-wise Job Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
            ...(showReportComponents && !isSubAdmin ? [{ to: '/home/job-wise-report', label: 'Job-wise Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
          ]
        }] : []),
      ],
    },
    ...(isTeamLeader ? [{
      id: 'management',
      label: 'MANAGEMENT',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showDocumentComponents ? [{ to: '/home/documents', label: t('Documents'), icon: <DescriptionRoundedIcon fontSize="small" /> }] : []),
        ...(showInvoiceComponents ? [{ to: '/home/invoices', label: t('Invoices'), icon: <ReceiptRoundedIcon fontSize="small" /> }] : []),
        ...(showEmployeeMonitoring ? [{ to: '/home/employee-monitoring', label: 'Employee Monitoring', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
        ...(showHolidayManagement ? [{ to: '/home/holidays', label: 'Holidays', icon: <CalendarMonthRoundedIcon fontSize="small" /> }] : []),
      ],
    }] : []),
    {
      id: 'leave',
      label: 'LEAVE',
      defaultOpen: true,
      collapsible: true,
      items: [
        ...(showApplyLeave ? [{ to: '/home/apply-leave', label: 'Apply Leave', icon: <BeachAccessRoundedIcon fontSize="small" /> }] : []),
        ...(showLeaveApproval ? [{ to: '/home/leave-approval', label: 'Leave Approval', icon: <BeachAccessRoundedIcon fontSize="small" /> }] : []),
        ...(showMyLeaves ? [{ to: '/home/my-leaves', label: 'My Leaves', icon: <BeachAccessRoundedIcon fontSize="small" /> }] : []),
        ...(showLeaveHistory ? [{ to: '/home/leave-history', label: 'Leave History', icon: <HistoryRoundedIcon fontSize="small" /> }] : []),
        ...(showLeaveReport ? [{ to: '/home/leave-report', label: 'Leave Report', icon: <AssessmentRoundedIcon fontSize="small" /> }] : []),
        ...(showHolidayManagement ? [{ to: '/home/leave-balance', label: 'Leave Balance', icon: <AccountBalanceWalletRoundedIcon fontSize="small" /> }] : []),
      ],
    },
    ...(showMyReviews ? [{
      id: 'performance',
      label: 'PERFORMANCE',
      defaultOpen: true,
      collapsible: true,
      items: [
        { to: '/home/self-evaluation', label: 'Self Evaluation', icon: <AssessmentRoundedIcon fontSize="small" /> }
      ],
    }] : []),
  ].map(s => ({ ...s, items: (s.items || []).filter(Boolean) }));

  const notificationsNavItem = { to: '/home/notifications', label: 'Notifications', icon: <NotificationsRoundedIcon fontSize="small" /> };
  const overviewSection = navSections.find(s => s.id === 'overview');
  if (overviewSection) {
    overviewSection.items = [...overviewSection.items, notificationsNavItem];
  }

  return (
    <NotificationProvider userId={userId}>
      <DashboardLayout
        navSections={navSections}
        headerTitle="Workmate"
        workspaceLabel="Workspace"
        userName={userName}
        onLogout={logoutApp}
        showBack={showBackButton}
        onBack={goBack}
        bell={<NotificationBell />}
        sidebarBottom={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
              Signed in as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              {userName || 'User'}
            </Typography>
            <Button
              variant="contained"
              onClick={logoutApp}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
            >
              {t('home.logout')}
            </Button>
          </Box>
        }
      >
        <Routes>
          {!accessToken && <Route path="/login" element={<Login />} />}
          <Route path="/" element={userId ? <Dashboard userId={userId} /> : null} />
          <Route path="/dashboard" element={userId ? <Dashboard userId={userId} /> : null} />
          {showJobComponents && <Route path="/jobs" element={<Jobs />} />}
          {showClientTab && <Route path="/clients" element={<Clients />} />}
          {showTaskComponents && <Route path="/tasks" element={<Tasks />} />}
          {showTaskComponents && <Route path="/task" element={<TaskForm />} />}
          {showTaskComponents && <Route path="/task-time-track" element={<TaskTimeTrack />} />}
          {showTaskComponents && <Route path="/extra-task" element={<ExtraTaskForm />} />}
          {showTaskComponents && <Route path="/extra-tasks" element={<ExtraTasks />} />}
          {showUserComponents && <Route path="/user" element={<UserForm />} />}
          {showUserComponents && <Route path="/users" element={<Users />} />}
          {showJobComponents && <Route path="/job-status" element={<JobStatus />} />}
          {showJobComponents && <Route path="/job-track" element={<JobTrack />} />}
          {showClientTab && <Route path="/client" element={<ClientForm />} />}
          {showJobComponents && <Route path="/job" element={<JobForm />} />}
          {showReportComponents && <Route path="/client-job-report" element={<ClientWiseJobReport />} />}
          {(isAdmin || isSubAdmin || showReportComponentForTeamLeader) && <Route path="/job-status-report" element={<JobStatusReport />} />}
          {(isAdmin || showReportComponentForTeamLeader) && <Route path="/job-completion-report" element={<JobCompletionReport />} />}
          {(isAdmin || isSubAdmin) && <Route path="/missed-ex-date-report" element={<MissedExDateReport />} />}
          {isAdmin && <Route path="/employee-efficiency-report" element={<EmpEfficiencyReport />} />}
          {(isAdmin || isSubAdmin) && <Route path="/employee-extra-task-report" element={<EmpExtraTaskReport />} />}
          {showReportComponents && !isSubAdmin && <Route path="/employee-job-task-report" element={<EmpJobTaskReport />} />}
          {showReportComponentForTeamLeader && <Route path="/employee-wise-job-report" element={<EmpWiseJobReport />} />}
          {showReportComponents && !isSubAdmin && <Route path="/job-wise-report" element={<JobWiseReport />} />}
          {showReportComponents && <Route path="/reports" element={<Reports />} />}
          {showAssignedJobTab && <Route path="/assigned-job" element={<AssignedJob />} />}
          {showUnassignedJobTab && <Route path="/unassigned-job" element={<UnassignedJob />} />}
          {showUserComponents && <Route path="/teams" element={<Teams />} />}
          {showUserComponents && <Route path="/assistance-teams" element={<AssistanceTeams />} />}
          {showUserComponents && <Route path="/team-members" element={<TeamMembers />} />}
          <Route path="/profile" element={<Profile />} />
          {showDocumentComponents && <Route path="/documents" element={<DocumentForm />} />}
          {showDocumentComponents && <Route path="/documents/preview" element={<DocumentPreview />} />}
          {showInvoiceComponents && <Route path="/invoices" element={<InvoiceForm />} />}
          {showInvoiceComponents && <Route path="/invoices/preview" element={<InvoicePreview />} />}
          <Route path="/notifications" element={<NotificationsPage />} />
          {showApplyLeave && <Route path="/apply-leave" element={<ApplyLeave />} />}
          {showLeaveApproval && <Route path="/leave-approval" element={<LeaveApproval />} />}
          {showMyLeaves && <Route path="/my-leaves" element={<MyLeaves />} />}
          {showLeaveHistory && <Route path="/leave-history" element={<LeaveHistory />} />}
          {showLeaveReport && <Route path="/leave-report" element={<LeaveReport />} />}
          {showHolidayManagement && <Route path="/holidays" element={<HolidayManagement />} />}
          {showHolidayManagement && <Route path="/leave-balance" element={<LeaveBalanceManagement />} />}
          <Route path="/leave-balance-audit" element={<LeaveBalanceAuditPage />} />
          {showEmployeeMonitoring && <Route path="/employee-monitoring" element={<EmployeeMonitoring />} />}
          {showMyReviews && <Route path="/self-evaluation" element={<SelfEvaluation />} />}
          <Route path="/quarterly-review/:trackerId" element={<QuarterlyReviewForm />} />
        </Routes>
      </DashboardLayout>
    </NotificationProvider>
  );
};

export default Home;